<template>
  <m-dropdown
    v-if="show"
    class="sort-selector"
    block
    placement="bottomRight"
    :relocate-key="relocateKey"
    :title="$t('gridPageChartEditor.sortBy')"
  >
    <m-card-item icon="order">
      {{ $t('gridPageChartEditor.sortBy') }}
      <template #right>
        <chart-editor-setting-value :value="displayValue" />
      </template>
    </m-card-item>
    <template #overlay>
      <m-card
        no-padding
        class="sort-selector-overlay"
        list
      >
        <m-card-item
          v-for="option in options"
          :key="option.value"
          @click="selectSort(option.value)"
        >
          {{ option.text }}
          <template
            v-if="option.value === value"
            #right
          >
            <m-icon
              type="check"
              xs
            />
          </template>
        </m-card-item>
        <template v-if="displayGroups.length > 0">
          <div class="_groups-header">
            <span class="_groups-title">{{ groupsLabel || $t('gridPageChartEditor.categories') }}</span>
            <m-btn
              hide-border
              super-light
              xxs
              @click="toggleAllVisibility"
            >
              {{ allHidden ? $t('gridPageChartEditor.showAll') : $t('gridPageChartEditor.hideAll') }}
            </m-btn>
          </div>
          <div class="_groups-list">
            <component
              :is="isManual ? 'm-draggable' : 'div'"
              v-bind="isManual ? {
                'recreate-key': displayGroups.length,
                'draggable-item-class': '_group-item',
                'dragover-item-class': '_group-item',
                'scroll-container-class': '_groups-list',
                'can-drag-over-top': true,
                'can-drag-over-bottom': true,
                'drag-between-height': 12,
              } : {}"
              @set-drag-item="isManual && setDragItem($event)"
              @over-bottom="isManual && setOverBottom($event)"
              @over-top="isManual && setOverTop($event)"
              @drag-drop="isManual && onDrop()"
              @cancel="isManual && cancelDragging()"
            >
              <div
                v-for="(group, index) in displayGroups"
                :key="group.originalValue"
                :class="['_group-item', isHidden(group.originalValue) ? '-hidden' : '', isManual ? '-draggable' : '']"
                :data-id="group.originalValue"
              >
                <div
                  v-if="isManual && index === 0 && dragItemId !== '' && draggingOverTop.includes(group.originalValue)"
                  class="_drop-indicator -top"
                />
                <m-card-item
                  :icon="isManual ? 'drag' : undefined"
                  :icon-color="isManual ? $colors.grey.lighten1 : undefined"
                  :light="isHidden(group.originalValue)"
                  :clickable="false"
                >
                  {{ group.title }}
                  <template #right>
                    <m-btn
                      :icon="isHidden(group.originalValue) ? 'eye-invisible' : 'eye'"
                      xs
                      fab
                      hide-border
                      super-light
                      @click.stop="toggleVisibility(group.originalValue)"
                    />
                  </template>
                </m-card-item>
                <div
                  v-if="isManual && dragItemId !== '' && draggingOverBottom.includes(group.originalValue) && !draggingOverBottom.includes(dragItemId)"
                  class="_drop-indicator -bottom"
                />
              </div>
            </component>
          </div>
        </template>
      </m-card>
    </template>
  </m-dropdown>
</template>

<script setup>
import ChartEditorSettingValue from '@/components/custom-grid/ChartEditorSettingValue.vue';
import useSort from '@/composables/draggable/sort';
import { chartSortBy } from 'shared/constants.json';
import { computed } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: true,
  },
  options: {
    type: Array,
    required: true,
  },
  value: {
    type: String,
    required: true,
  },
  groups: {
    type: Array,
    default: () => [],
  },
  manualOrder: {
    type: Array,
    default: () => [],
  },
  hiddenGroups: {
    type: Array,
    default: () => [],
  },
  groupsLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:value', 'update:manualOrder', 'update:hiddenGroups']);

const isManual = computed(() => props.value === chartSortBy.manual);

const displayValue = computed(() => props.options.find((o) => o.value === props.value)?.text);

const relocateKey = computed(() => `${props.value}-${displayGroups.value.length}`);

const selectSort = (val) => {
  emit('update:value', val);
};

const displayGroups = computed(() => {
  if (props.groups.length === 0) return [];
  let ordered;
  if (!isManual.value || props.manualOrder.length === 0) {
    ordered = props.groups;
  } else {
    const orderMap = new Map(props.manualOrder.map((val, i) => [String(val), i]));
    const inOrder = props.groups.filter((g) => orderMap.has(String(g.originalValue)));
    const rest = props.groups.filter((g) => !orderMap.has(String(g.originalValue)));
    inOrder.sort((a, b) => orderMap.get(String(a.originalValue)) - orderMap.get(String(b.originalValue)));
    ordered = [...inOrder, ...rest];
  }
  const visible = ordered.filter((g) => !isHidden(g.originalValue));
  const hidden = ordered.filter((g) => isHidden(g.originalValue));
  return [...visible, ...hidden];
});

const {
  dragItemId,
  draggingOverBottom,
  draggingOverTop,
  setDragItem,
  setOverBottom,
  setOverTop,
  dropItem,
  cancelDragging,
} = useSort('originalValue');

const onDrop = () => {
  const newOrder = dropItem(displayGroups.value);
  emit('update:manualOrder', newOrder.map((g) => String(g.originalValue)));
};

const isHidden = (originalValue) => props.hiddenGroups.includes(String(originalValue));

const allHidden = computed(() => displayGroups.value.length > 0 && displayGroups.value.every((g) => isHidden(g.originalValue)));

const toggleVisibility = (originalValue) => {
  const str = String(originalValue);
  if (isHidden(originalValue)) {
    emit('update:hiddenGroups', props.hiddenGroups.filter((v) => String(v) !== str));
  } else {
    emit('update:hiddenGroups', [...props.hiddenGroups, str]);
  }
};

const toggleAllVisibility = () => {
  if (allHidden.value) {
    emit('update:hiddenGroups', []);
  } else {
    emit('update:hiddenGroups', displayGroups.value.map((g) => String(g.originalValue)));
  }
};
</script>

<style lang="scss">
.sort-selector-overlay {
  min-width: 22rem;
  padding-bottom: .4rem;

  ._groups-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: .4rem .8rem;
    border-top: 1px solid $border-color;
    margin-top: .4rem;
  }

  ._groups-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: $font-color-secondary;
    text-transform: uppercase;
    margin-right: 1.4rem;
    margin-top: 0.1rem;
  }

  ._groups-list {
    display: flex;
    flex-direction: column;
    max-height: 30vh;
    overflow-y: auto;
  }
}

._group-item {
  position: relative;
  background: white;

  &.-draggable {
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  &.-hidden {
    ._left {
      color: $font-color-tertiary;
      text-decoration: line-through;
    }
  }

  ._drop-indicator {
    position: absolute;
    right: 0;
    left: 0;
    z-index: 88;
    height: 3px;
    pointer-events: none;
    background: $highlighted-color-dark;

    &.-top {
      top: 0;
    }

    &.-bottom {
      bottom: 0;
    }
  }
}
</style>
